/**
 * THE DEEP PIVOT SCAN, ON THE CHART.
 *
 * <h2>Why this is a builder and not a renderer</h2>
 *
 * `chartDataProcessor`'s `pivotmarkers` branch already draws exactly what this study needs: a
 * scatter series with a per-point symbol, rotation, colour, placement above or below the bar, an
 * adjacent label and a per-point tooltip. It is also the CHOKEPOINT that forces a projected mark
 * to render as a gold circle rather than an outcome-coloured triangle. A second renderer for these
 * arrows would duplicate all of that and quietly opt out of the chokepoint, so this module only
 * turns scan rows into points and lets that branch draw them.
 *
 * <h2>Bar INDEX by date, not a Unix timestamp</h2>
 *
 * The brief asks for alignment "with that bar's Unix timestamp". The price grid's x axis is a
 * CATEGORY axis whose categories are the `YYYY-MM-DD` strings in `barData.dates` — so a point is
 * placed by handing the renderer that same string, and a timestamp would match no category at all.
 * ECharts does not complain about that: the point is silently dropped, and the study would appear
 * to have found nothing on a chart full of candles.
 *
 * <p>Which is also why a date absent from the series is filtered out HERE and counted, rather than
 * being left to vanish. The scan runs over the symbol's whole stored history; the chart may be
 * showing a padded or truncated series, and "14 pivots were not drawn" is a fact the operator
 * needs rather than a silent discrepancy between the count and the canvas.
 */

/** One id, so a re-scan replaces the previous paint instead of stacking on it. */
export const DEEP_PIVOT_OVERLAY_ID = 'deep-pivot-markers';

/**
 * Subdued bronze, as specified — and deliberately the ONLY colour these markers use.
 *
 * <p>Direction is already carried twice: the triangle points down for a high and up for a low, and
 * it sits above the high wick or below the low one. Spending the colour channel on a third copy of
 * the same fact would leave nothing to say with it later, and a reader would still have to learn
 * which hue meant which. Distinct from the trade cards' navy and charcoal, and from the yearly
 * plaque's #D4A017 gold, so three overlays can share a chart without a legend.
 */
export const DEEP_PIVOT_COLOR = '#8B7355';

/** The same stack the chart's own tooltip body uses, so the two cannot drift apart. */
export const DEEP_PIVOT_MONO = 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace';

/** A pivot carrying no harmonic tag is rendered as this in the report. */
const UNTAGGED = '—';

/**
 * What each condition actually tested, in the tooltip's own words.
 *
 * <p>Spelled out rather than left as "Condition 2", because the number is an ordering of the
 * sequential match and says nothing about what was found. A reader looking at a B2 wants to know
 * it is one planet standing in two longitudinal aspects.
 */
export const CONDITION_DESCRIPTIONS = {
  1: 'trine + conjunction/square/opposition, sharing a planet',
  2: 'one planet in TWO longitudinal aspects',
  3: 'one planet in one longitudinal + one declination aspect',
};

const isTagged = (row) => !!row
  && typeof row.category === 'string'
  && row.category !== ''
  && row.category !== UNTAGGED
  && Number(row.condition) >= 1;

const deg = (v) => (Number.isFinite(v) ? `${v.toFixed(2)}°` : '—');

/** Left-pads the gutter so the tooltip's `white-space: pre` renders as two columns. */
const line = (label, value) => `${String(label).padEnd(10)}${value}`;

/**
 * The math that painted this arrow.
 *
 * <h3>The tier quantity is condition-dependent, and showing the wrong one would mislead</h3>
 *
 * Conditions 1 and 2 band on the DIFFERENCE between the two orbs; condition 3 bands on the LARGER
 * of them — the specification's own wording, and already pinned by the CSV test, which ships both
 * numbers so the table can be re-tiered offline. A tooltip that always showed the difference would
 * print, on every C row, a number that had nothing to do with the tier beside it. So the line that
 * set the band is the one rendered, and it says so.
 *
 * <p>Multi-line and column-aligned rather than the brief's single piped string: the renderer's
 * formatter already emits these in a monospaced `white-space: pre` block, and five labelled rows
 * are easier to read against a candle than one sixty-character line. Same facts, laid out.
 */
export const deepPivotTooltipLines = (row) => {
  if (!row) return [];
  const condition = Number(row.condition);
  const banded = condition === 3
    ? line('Max orb', `${deg(row.maxOrb)}   <- set the tier`)
    : line('Orb diff', `${deg(row.orbDifference)}   <- set the tier`);

  const legParts = [];
  if (Number.isFinite(row.legBars)) legParts.push(`${row.legBars} bars`);
  if (Number.isFinite(row.legPercent)) {
    legParts.push(`${row.legPercent >= 0 ? '+' : ''}${row.legPercent.toFixed(2)}%`);
  }
  if (row.structure) legParts.push(row.structure);

  return [
    line('Condition', `${condition} — ${CONDITION_DESCRIPTIONS[condition] || 'unclassified'}`),
    line('Aspect 1', row.aspect1 || '—'),
    line('Aspect 2', row.aspect2 || '—'),
    line('Shared', row.sharedPlanet || '—'),
    banded,
    line('Leg', legParts.length ? legParts.join(' · ') : '—'),
    line('Price', Number.isFinite(row.price) ? row.price.toFixed(4) : '—'),
  ];
};

/**
 * THE OVERLAY.
 *
 * @param report the scan response — `{ rows: [...] }`
 * @param dates  the chart's own `barData.dates`, which decide what can be drawn at all
 * @returns an overlay carrying `drawnCount`, always — the caller reports on it, and a null here
 *          would make "the scan found nothing" and "the scan was never run" the same thing
 */
export const deepPivotOverlay = (report, dates) => {
  const rows = report && Array.isArray(report.rows) ? report.rows : [];
  const onChart = new Set(Array.isArray(dates) ? dates : []);

  let untagged = 0;
  let offChart = 0;
  const points = [];

  rows.forEach((row) => {
    if (!isTagged(row)) { untagged += 1; return; }
    if (!onChart.has(row.date)) { offChart += 1; return; }
    if (!Number.isFinite(row.price)) { offChart += 1; return; }

    // The pivot's own extreme: its HIGH for a high, its LOW for a low. So anchoring here and
    // asking for 'above'/'below' puts the arrow clear of the wick rather than clear of the close,
    // which on a long-wicked bar is a different place entirely.
    const high = row.pivotType === 'HIGH';
    points.push({
      date: row.date,
      price: row.price,
      position: high ? 'above' : 'below',
      symbol: 'triangle',
      // A triangle points UP unrotated. A high pivot gets the 180 so the arrow points back down
      // at the bar it belongs to, which is what makes the mark readable without the label.
      symbolRotate: high ? 180 : 0,
      color: DEEP_PIVOT_COLOR,
      // ADJACENT, not inside. Two characters inside a triangle is the shape's narrowest part and
      // at 10px they overflow it — the brief allows either, and the white chip the renderer puts
      // behind an outside label is the higher contrast of the two anyway.
      label: row.category,
      title: `${row.category} · ${row.pivotType === 'HIGH' ? 'High' : 'Low'} pivot`,
      tooltipLines: deepPivotTooltipLines(row),
      tooltipFooter: row.date,
    });
  });

  return {
    type: 'pivotMarkers',
    id: DEEP_PIVOT_OVERLAY_ID,
    points,
    color: DEEP_PIVOT_COLOR,
    label: 'Deep Pivot tags',
    // DELIBERATELY NOT `astroTooltip`. That flag gates on `suppressAstroTooltips`, which is
    // `shortBarMode || barFloaterMode === 'ohlcv'` -- and the floater mode DEFAULTS to 'ohlcv',
    // so opting in suppresses the card for everybody out of the box. Setting it here is exactly
    // the mistake the spiral arcs already made and recorded; short-bar mode is enforced
    // absolutely in the renderer instead, on `shortBarMode` itself.
    source: 'deepPivot',
    symbol: 'triangle',
    symbolSize: 9,
    // Clear of the wick. The default 12 sits a mark almost on it, and these arrows point AT the
    // bar — a gap is what makes the pointing legible.
    symbolOffsetPx: 14,
    labelFontSize: 10,
    labelFontFamily: DEEP_PIVOT_MONO,
    drawnCount: points.length,
    untaggedCount: untagged,
    offChartCount: offChart,
  };
};

/** What to put in the status line. Says what was NOT drawn, which is the half that gets lost. */
export const deepPivotStatus = (symbol, overlay) => {
  if (!overlay || overlay.drawnCount === 0) {
    return `Deep Pivot: nothing to draw for ${symbol}`;
  }
  const bits = [`Deep Pivot: ${overlay.drawnCount} tagged pivots on ${symbol}`];
  if (overlay.untaggedCount) bits.push(`${overlay.untaggedCount} untagged`);
  if (overlay.offChartCount) bits.push(`${overlay.offChartCount} off-chart`);
  return bits.join(' · ');
};
